import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const footerCategories = categories.slice(0, 8);
  const socialLinks = [
    { icon: 'ri-twitter-x-line', url: '#' },
    { icon: 'ri-instagram-line', url: '#' },
    { icon: 'ri-telegram-line', url: '#' },
    { icon: 'ri-discord-line', url: '#' },
  ];

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d8pb8q18gb8kvsd0oit0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setTimeout(() => setSubmitStatus('idle'), 4000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 4000);
      }
    } catch {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 4000);
    }
  };

  return (
    <footer className="bg-foreground-900 pt-16 md:pt-20 pb-8">
      <div className="max-w-[1280px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-12">
          <Link
            to="/"
            className="font-heading text-2xl font-bold lowercase tracking-tight text-white"
          >
            cssbuy<span className="text-primary-400">.today</span>
          </Link>
          <p className="hidden md:block text-foreground-400 text-sm max-w-md text-right">
            {t('footer.brand_desc')}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-14">
          <div>
            <h4 className="text-white font-semibold text-sm mb-4">{t('footer.column1_title')}</h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/" className="text-foreground-400 text-sm hover:text-white transition-colors duration-150">
                  Início
                </Link>
              </li>
              <li>
                <Link to="/guia-cssbuy-completo" className="text-foreground-400 text-sm hover:text-white transition-colors duration-150">
                  Guia Completo
                </Link>
              </li>
              <li>
                <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-foreground-400 text-sm hover:text-white transition-colors duration-150">
                  Spreadsheet
                </a>
              </li>
              <li>
                <Link to="/faq-cssbuy" className="text-foreground-400 text-sm hover:text-white transition-colors duration-150">
                  FAQ
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-4">{t('footer.column2_title')}</h4>
            <ul className="space-y-2.5">
              {footerCategories.map((cat) => (
                <li key={cat.slug}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-foreground-400 text-sm hover:text-white transition-colors duration-150"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-4">{t('footer.column3_title')}</h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/guia-cssbuy-completo" className="text-foreground-400 text-sm hover:text-white transition-colors duration-150">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/seguranca-cssbuy" className="text-foreground-400 text-sm hover:text-white transition-colors duration-150">
                  Segurança
                </Link>
              </li>
              <li>
                <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-foreground-400 text-sm hover:text-white transition-colors duration-150">
                  Buy It Now
                </a>
              </li>
              <li>
                <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-foreground-400 text-sm hover:text-white transition-colors duration-150">
                  CSSBuy Spreadsheet
                </a>
              </li>
            </ul>
          </div>

          <div className="col-span-2 md:col-span-1">
            <h4 className="text-white font-semibold text-lg mb-1">
              © CSSBuy.today 2026
            </h4>
            <p className="text-foreground-400 text-xs font-heading italic mb-6">
              {t('footer.rights')}
            </p>
            <div className="flex gap-4 mb-8">
              {socialLinks.map((link) => (
                <a
                  key={link.icon}
                  href={link.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-foreground-400 hover:text-primary-400 transition-colors duration-150 underline"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${link.icon} text-sm`}></i>
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-foreground-800 pt-8">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-start">
            <div className="w-full lg:w-[200px] h-[130px] rounded-xl overflow-hidden shrink-0">
              <img
                src="https://readdy.ai/api/search-image?query=Cozy%20minimal%20interior%20with%20a%20comfortable%20armchair%20and%20a%20small%20side%20table%20with%20a%20laptop%2C%20warm%20natural%20light%20from%20a%20large%20window%2C%20few%20fashion%20magazines%20scattered%2C%20scandinavian%20design%20aesthetic%2C%20cream%20and%20beige%20tones%2C%20peaceful%20lifestyle%20photography&width=400&height=260&seq=footer-img-v2-e4f5&orientation=landscape"
                alt="CSSBuy Community"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex-1">
              <p className="font-heading text-foreground-300 text-lg md:text-xl leading-relaxed italic">
                O melhor guia CSSBuy <br />
                spreadsheet em português. <br />
                Atualizado semanalmente.
              </p>
            </div>

            <div className="w-full lg:w-[340px]">
              <p className="text-foreground-400 text-xs mb-1">{t('footer.newsletter_title')}</p>
              <p className="text-foreground-500 text-xs mb-3">{t('footer.newsletter_desc')}</p>
              <form
                onSubmit={handleNewsletter}
                action="https://readdy.ai/api/form/d8pb8q18gb8kvsd0oit0"
                method="POST"
                encType="application/x-www-form-urlencoded"
                data-readdy-form
                className="flex gap-2"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer.newsletter_placeholder')}
                  className="flex-1 px-4 py-2.5 rounded-full bg-foreground-800 border border-foreground-700 text-white text-sm placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors duration-200"
                />
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-400 transition-colors duration-200 whitespace-nowrap flex items-center gap-1.5"
                >
                  {t('footer.newsletter_button')}
                  <i className="ri-arrow-right-line text-sm"></i>
                </button>
              </form>
              {submitStatus === 'success' && (
                <p className="mt-2 text-accent-400 text-xs font-medium">Inscrição confirmada! Obrigado.</p>
              )}
              {submitStatus === 'error' && (
                <p className="mt-2 text-red-400 text-xs font-medium">Erro ao inscrever. Tente novamente.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}